import React from 'react';
import { renderToString } from 'react-dom/server';
import { StaticRouter } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { matchRoutes } from 'react-router-config';
import { Helmet } from 'react-helmet';
import { ServerStyleSheet } from 'styled-components';
import serialize from 'serialize-javascript';
import minifyCssString from 'minify-css-string';
import mapJson from 'jsonpath-mapper';
import { Express, Request, Response } from 'express';
import { StaticRouterContext } from 'react-router';
import { ChunkExtractorManager } from '@loadable/server';
import { identity, noop } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { buildCleaner } from 'lodash-clean';
import { CookiesProvider } from 'react-cookie';
import Cookies from 'universal-cookie';
import cookiesMiddleware from 'universal-cookie-express';

import createStore from '~/redux/store/store';
import { history } from '~/redux/store/history';
import rootSaga from '~/redux/sagas';

import { setVersion, setVersionStatus } from '~/redux/actions/version';
import { setCurrentProject } from '~/routing/redux/actions';
import { selectCurrentSearch } from '~/routing/redux/selectors';

import { deliveryApi } from '~/util/ContensisDeliveryApi';
import pickProject from '~/util/pickProject';
import stringifyAttributes from './util/stringifyAttributes';

import { getCacheDuration } from './features/caching/cacheDuration.schema';
import handleResponse from './features/response-handler';

import {
  getBundleData,
  getBundleTags,
  loadableChunkExtractors,
} from './util/bundles';
import { addStandardHeaders } from './util/headers';

import { AppState, ServerConfig } from '~/models';
import { getVersionInfo } from './util/getVersionInfo';
import { unhandledExceptionHandler } from './util/handleExceptions';
import { SSRContextProvider } from '~/util/SSRContext';

const webApp = (
  app: Express,
  ReactApp: React.ComponentType<any>,
  config: ServerConfig & {
    allowedGroups?: string[];
    globalGroups?: string[];
    startupScriptFilename?: string;
  }
) => {
  const {
    stateType = 'immutable',
    routes,
    withReducers,
    withSagas,
    withEvents,
    packagejson,
    scripts = {},
    staticFolderPath = 'static',
    startupScriptFilename,
    allowedGroups,
    globalGroups,
    disableSsrRedux,
    enableSsrCookies,
    handleResponses,
    handleExceptions = true,
  } = config;
  const staticRoutePath = config.staticRoutePath || staticFolderPath;

  const bundleData = getBundleData(config, staticRoutePath);

  const attributes = stringifyAttributes(scripts.attributes);
  scripts.startup = scripts.startup || startupScriptFilename;

  const responseHandler =
    typeof handleResponses === 'function' ? handleResponses : handleResponse;

  if (handleExceptions !== false) unhandledExceptionHandler(handleExceptions); // Create `process.on` event handlers for unhandled exceptions (Node v15+)

  const versionInfo = getVersionInfo(staticFolderPath);

  app.get(
    '/*',
    cookiesMiddleware(),
    async (
      request: Request & {
        universalCookies?: Cookies;
      },
      response: Response
    ) => {
      const url = encodeURI(request.url);

      const matchedStaticRoute = () =>
        matchRoutes(routes.StaticRoutes, request.path);
      const isStaticRoute = () => matchedStaticRoute().length > 0;
      const staticRoute = isStaticRoute() && matchedStaticRoute()[0];

      // Allow certain routes to avoid SSR
      const onlyDynamic = staticRoute && staticRoute.route.ssr === false;
      const onlySSR = staticRoute && staticRoute.route.ssrOnly === true;

      const normaliseQs = q => (q && q.toLowerCase() === 'true' ? true : false);

      // Determine functional params from QueryString and set access methods
      const accessMethod = mapJson<
        any,
        { DYNAMIC: boolean; REDUX: boolean; FRAGMENT: boolean; STATIC: boolean }
      >(request.query, {
        DYNAMIC: ({ dynamic }) => normaliseQs(dynamic) || onlyDynamic,
        REDUX: ({ redux }) => normaliseQs(redux),
        FRAGMENT: ({ fragment }) => normaliseQs(fragment),
        STATIC: ({ static: value }) => normaliseQs(value) || onlySSR,
      });

      const context: StaticRouterContext = {};
      // Track the current statusCode via the response object
      response.status(200);

      // Create a store (with a memory history) from our current url
      const store = await createStore(
        withReducers,
        {} as AppState,
        history({
          initialEntries: [url],
        }),
        stateType
      );

      // dispatch any global and non-saga related actions before calling our JSX
      const versionStatus = deliveryApi.getServerSideVersionStatus(request);

      // In server-side blocks world, the hostname requested by the client resides in the x-orig-host header
      // Because of this, we prioritize x-orig-host when setting our hostname
      const hostname = (request.headers['x-orig-host'] ||
        request.hostname) as string;

      console.info(
        `Request for ${request.path} hostname: ${hostname} versionStatus: ${versionStatus}`
      );

      store.dispatch(setVersionStatus(versionStatus));
      store.dispatch(setVersion(versionInfo.commitRef, versionInfo.buildNo));

      const project = pickProject(hostname, request.query);

      const groups = allowedGroups && allowedGroups[project];
      store.dispatch(setCurrentProject(project, groups, hostname));

      const loadableExtractor = loadableChunkExtractors();

      const ssrCookies = enableSsrCookies
        ? // these cookies are managed by the cookiesMiddleware and contain listeners
          // when cookies are read or written in ssr can be added to the `set-cookie` response header
          request.universalCookies
        : // this is a stub cookie collection so cookie methods can be used in code
          new Cookies();

      const jsx = (
        <ChunkExtractorManager
          extractor={loadableExtractor.commonLoadableExtractor}
        >
          <CookiesProvider cookies={ssrCookies}>
            <ReduxProvider store={store}>
              <StaticRouter context={context} location={url}>
                <SSRContextProvider request={request} response={response}>
                  <ReactApp routes={routes} withEvents={withEvents} />
                </SSRContextProvider>
              </StaticRouter>
            </ReduxProvider>
          </CookiesProvider>
        </ChunkExtractorManager>
      ) as React.ReactElement;

      const {
        templateHTML = '',
        templateHTMLFragment = '',
        templateHTMLStatic = '',
      } = bundleData.default.templates || bundleData.legacy.templates || {};

      // Serve a blank HTML page with client scripts to load the app in the browser
      if (accessMethod.DYNAMIC) {
        // Dynamic doesn't need sagas
        renderToString(jsx);

        // Dynamic page render has only the necessary bundles to start up the app
        // and does not include any react-loadable code-split bundles
        const bundleTags = getBundleTags(
          loadableExtractor,
          scripts,
          staticRoutePath
        );

        const isDynamicHints = `<script ${attributes}>window.versionStatus = "${versionStatus}"; window.isDynamic = true;</script>`;

        const responseHtmlDynamic = templateHTML
          .replace('{{TITLE}}', '')
          .replace('{{SEO_CRITICAL_METADATA}}', '')
          .replace('{{CRITICAL_CSS}}', '')
          .replace('{{APP}}', '')
          .replace('{{LOADABLE_CHUNKS}}', bundleTags)
          .replace('{{REDUX_DATA}}', isDynamicHints);
        // Dynamic pages always return a 200 so we can run
        // the app and serve up all errors inside the client
        response.setHeader(
          'Surrogate-Control',
          `max-age=${getCacheDuration(200)}`
        );
        responseHandler(request, response, responseHtmlDynamic);
      }

      // Render the JSX server side and send response as per access method options
      if (!accessMethod.DYNAMIC) {
        store
          .runSaga(rootSaga(withSagas))
          .toPromise()
          .then(() => {
            const sheet = new ServerStyleSheet();

            const html = renderToString(sheet.collectStyles(jsx) as React.ReactElement);

            const helmet = Helmet.renderStatic();
            Helmet.rewind();
            const htmlAttributes = helmet.htmlAttributes.toString();
            let title = helmet.title.toString();
            const metadata = helmet.meta
              .toString()
              .concat(helmet.base.toString())
              .concat(helmet.link.toString())
              .concat(helmet.script.toString())
              .concat(helmet.noscript.toString());

            if (context.url) {
              return response.redirect(context.statusCode || 302, context.url);
            }

            const reduxState = store.getState();

            const styleTags = sheet.getStyleTags();

            // After running rootSaga there should be an additional react-loadable
            // code-split bundles for any page components as well as core app bundles
            const bundleTags = getBundleTags(
              loadableExtractor,
              scripts,
              staticRoutePath
            );

            let clonedState = buildCleaner({
              isArray: identity,
              isBoolean: identity,
              isDate: identity,
              isFunction: noop,
              isNull: identity,
              isPlainObject: identity,
              isString: identity,
              isUndefined: noop,
            })(cloneDeep(reduxState));
            // These keys are used for preparing server-side response headers only
            // and are not required in the client at all except for debugging ssr
            if (!selectCurrentSearch(reduxState)?.includes('includeApiCalls')) {
              if (stateType === 'immutable')
                clonedState = clonedState
                  .deleteIn(['routing', 'apiCalls'])
                  .deleteIn(['routing', 'surrogateKeys']);
              else {
                delete clonedState.routing.apiCalls;
                delete clonedState.routing.surrogateKeys;
              }
            }
            // Reset user state to prevent user details from being cached in SSR
            if (stateType === 'immutable') {
              clonedState = clonedState.delete('user');
            } else delete clonedState.user;

            let serialisedReduxData = serialize(clonedState);
            if (context.statusCode !== 404) {
              // For a request that returns a redux state object as a response
              if (accessMethod.REDUX) {
                addStandardHeaders(reduxState, response, packagejson, {
                  allowedGroups,
                  globalGroups,
                });
                responseHandler(request, response, serialisedReduxData, 'json');
                return true;
              }
              if (!disableSsrRedux) {
                // window.versionStatus is not strictly required here and is added to support cases
                // where a consumer may not be using the contensisVersionStatus in redux and calling
                // the `getClientSideVersionStatus()` method directly
                serialisedReduxData = `<script ${attributes}>window.versionStatus = "${versionStatus}"; window.REDUX_DATA = ${serialisedReduxData}</script>`;
              }
            }
            if ((context.statusCode || 200) >= 404) {
              accessMethod.STATIC = true;
            }

            // Responses
            let responseHTML = '';

            if (context.statusCode === 404)
              title = '<title>404 page not found</title>';

            // Static page served as a fragment
            if (accessMethod.FRAGMENT && accessMethod.STATIC) {
              responseHTML = minifyCssString(styleTags) + html;
            }

            // Page fragment served with client scripts and redux data that hydrate the app client side
            if (accessMethod.FRAGMENT && !accessMethod.STATIC) {
              responseHTML = templateHTMLFragment
                .replace('{{TITLE}}', title)
                .replace('{{SEO_CRITICAL_METADATA}}', metadata)
                .replace('{{CRITICAL_CSS}}', minifyCssString(styleTags))
                .replace('{{APP}}', html)
                .replace('{{LOADABLE_CHUNKS}}', bundleTags)
                .replace('{{REDUX_DATA}}', serialisedReduxData);
            }

            // Full HTML page served statically
            if (!accessMethod.FRAGMENT && accessMethod.STATIC) {
              responseHTML = templateHTMLStatic
                .replace('{{TITLE}}', title)
                .replace('{{SEO_CRITICAL_METADATA}}', metadata)
                .replace('{{CRITICAL_CSS}}', minifyCssString(styleTags))
                .replace('{{APP}}', html)
                .replace('{{LOADABLE_CHUNKS}}', '');
            }

            // Full HTML page served with client scripts and redux data that hydrate the app client side
            if (!accessMethod.FRAGMENT && !accessMethod.STATIC) {
              responseHTML = templateHTML
                .replace('{{TITLE}}', title)
                .replace('{{SEO_CRITICAL_METADATA}}', metadata)
                .replace('{{CRITICAL_CSS}}', styleTags)
                .replace('{{APP}}', html)
                .replace('{{LOADABLE_CHUNKS}}', bundleTags)
                .replace('{{REDUX_DATA}}', serialisedReduxData);
            }

            // Set response.status from React StaticRouter
            if (typeof context.statusCode === 'number')
              response.status(context.statusCode);

            addStandardHeaders(reduxState, response, packagejson, {
              allowedGroups,
              globalGroups,
            });
            try {
              // If react-helmet htmlAttributes are being used,
              // replace the html tag with those attributes sepcified
              // e.g. (lang, dir etc.)
              if (htmlAttributes) {
                responseHTML = responseHTML.replace(
                  /<html?.+?>/,
                  `<html ${htmlAttributes}>`
                );
              }
              responseHandler(request, response, responseHTML);
            } catch (err: any) {
              console.info(err.message);
            }
          })
          .catch(err => {
            // Handle any error that occurred in any of the previous
            // promises in the chain.
            console.info(err);
            response.status(500);
            responseHandler(
              request,
              response,
              `Error occurred: <br />${err.stack} <br />${JSON.stringify(err)}`
            );
          });
        renderToString(jsx);

        store.close();
      }
    }
  );
};

export default webApp;
